import React, { createContext, useState, useCallback, useMemo, useRef } from 'react';
import { localStorageKeys } from '../../utils/constants';
import { onLoadRegBranchInformationAPI } from '../api-services/OnLoadRegBranchInformation.service';
import { regBranchPhoneVerificationAPI } from '../api-services/RegBranchPhoneVerify.service';
import { getUrlPath } from '../../utils/getUrls';
import {
  getOnLoadRegBranchPayloadFromStorage,
  getStoredAccessToken,
} from '../../utils/regBranchOnLoadPayload';
import { saveRegBranchOnLoadCache } from '../../utils/regBranchOnLoadCache';
import { clearBranchSessionStorage, requestLogoutBranch } from '../../utils/branchSessionLogout';

export const GlobalStateDataContext = createContext();

function hasSessionToken() {
  const { TOKEN, LOGIN_INFO } = localStorageKeys;
  if (localStorage.getItem(TOKEN)) return true;
  try {
    const li = localStorage.getItem(LOGIN_INFO);
    if (!li) return false;
    return !!JSON.parse(li).token;
  } catch {
    return false;
  }
}

/** Normalized session objects from last successful OnLoadRegBranchInformation response. */
function parseRegBranchOnLoadData(wrapped) {
  const inner = wrapped?.data;
  if (!inner || inner.success !== true || !inner.data) return null;
  const d = inner.data;
  return {
    regionalBranchInfo: d.RegionalBranchInfo ?? d.regionalBranchInfo ?? null,
    teamInfo: d.TeamInfo ?? d.teamInfo ?? null,
    teamRoleAccess: d.TeamRoleAccess ?? d.teamRoleAccess ?? null,
  };
}

const GlobalStateDataProvider = ({ children }) => {
  const checkUrl = getUrlPath() === '/';

  const [isLoggedIn, setIsLoggedIn] = useState(hasSessionToken);
  const [selectedMenuTab, setSelectedMenuTab] = useState(checkUrl && '');
  const [statsDataFromSocket, setStatsDataFromSocket] = useState(null); // Storing stats from ConnectedUser + OrderManagemen
  const [orderFromSocket, setOrderFromSocket] = useState([]);

  const [onLoadRegBranchResponse, setOnLoadRegBranchResponse] = useState({
    loading: false,
    success: null,
    message: null,
    data: null,
  });

  const [onPhoneVerifyResponse, setOnPhoneVerifyResponse] = useState({
    loading: false,
    success: null,
    message: null,
    data: null,
  });

  /** Prevents duplicate foreground OnLoad for the same token; cleared on failure / retry / logout. */
  const lastOnLoadRegBranchTokenRef = useRef(null);

  const refreshRegBranchOnLoadInBackground = useCallback(async (payload) => {
    try {
      const data = await onLoadRegBranchInformationAPI(payload);
      if (data.hasError || data.data?.success !== true) return { ok: false };
      const token = getStoredAccessToken();
      if (token) {
        await saveRegBranchOnLoadCache(token, data.data);
      }
      setOnLoadRegBranchResponse((prev) => ({
        loading: false,
        success: true,
        message: data.data?.message ?? prev.message,
        data,
      }));
      setIsLoggedIn(hasSessionToken());
      return { ok: true };
    } catch {
      return { ok: false };
    }
  }, []);

  const OnLoadRegBranchInformation = useCallback(async (payload) => {
    try {
      setOnLoadRegBranchResponse({
        loading: true,
        success: null,
        message: null,
        data: null,
      });

      const data = await onLoadRegBranchInformationAPI(payload);

      if (data.hasError) {
        const errMsg =
          typeof data.message === 'string' && data.message.trim()
            ? data.message.trim()
            : data.error?.message || 'Request failed';
        lastOnLoadRegBranchTokenRef.current = null;
        setOnLoadRegBranchResponse({
          loading: false,
          data: null,
          success: false,
          message: errMsg,
        });
        return { ok: false, message: errMsg };
      }

      if (data.data?.success === true) {
        const token = getStoredAccessToken();
        if (token) {
          await saveRegBranchOnLoadCache(token, data.data);
        }
        setOnLoadRegBranchResponse({
          loading: false,
          data,
          success: true,
          message: data.data?.message ?? null,
        });
        lastOnLoadRegBranchTokenRef.current = token;
        setIsLoggedIn(hasSessionToken());
        return { ok: true };
      }

      const msg =
        data.data?.errors?.CustomMessage ??
        data.data?.CustomMessage ??
        data.data?.message ??
        'Backend validation errors';

      lastOnLoadRegBranchTokenRef.current = null;
      setOnLoadRegBranchResponse({
        loading: false,
        data,
        success: data.data?.success === false ? false : null,
        message: msg,
      });
      setIsLoggedIn(hasSessionToken());
      return { ok: false, message: msg };
    } catch (error) {
      console.log('OnLoadRegBranchInformation catch', error);
      lastOnLoadRegBranchTokenRef.current = null;
      const msg = error?.message ?? 'Something went wrong';
      setOnLoadRegBranchResponse({
        loading: false,
        data: null,
        success: false,
        message: msg,
      });
      return { ok: false, message: msg };
    }
  }, []);

  const retryOnLoadRegBranchInformation = useCallback(() => {
    lastOnLoadRegBranchTokenRef.current = null;
    const p = getOnLoadRegBranchPayloadFromStorage();
    const t = getStoredAccessToken();
    if (p && t) {
      lastOnLoadRegBranchTokenRef.current = t;
      void OnLoadRegBranchInformation(p);
    }
  }, [OnLoadRegBranchInformation]);

  const OnPhoneVerify = useCallback(async (payload) => {
    try {
      setOnPhoneVerifyResponse({
        loading: true,
        success: null,
        message: null,
        data: null,
      });

      const data = await regBranchPhoneVerificationAPI(payload);
      if (data.hasError) {
        const errMsg =
          typeof data.message === 'string' && data.message.trim()
            ? data.message.trim()
            : (data.message?.CustomMessage ??
              data.data?.CustomMessage ??
              data.error?.message ??
              'Request failed');
        setOnPhoneVerifyResponse({
          loading: false,
          data: null,
          success: false,
          message: errMsg,
        });
        return;
      }

      if (data.data?.success) {
        setOnPhoneVerifyResponse({
          loading: false,
          data: data,
          success: data.data?.success,
          message: data.data?.CustomMessage,
        });
        return data;
      }

      setOnPhoneVerifyResponse({
        loading: false,
        data: data,
        success: data.data?.success,
        message: data.data?.CustomMessage,
      });
      return data;
    } catch (error) {
      console.log('OnPhoneVerify catch', error);
      setOnPhoneVerifyResponse({
        loading: false,
        data: null,
        success: false,
        message: error?.message ?? 'Something went wrong',
      });
    }
  }, []);

  const branchSessionLogout = useCallback(async (navigate) => {
    await requestLogoutBranch();
    clearBranchSessionStorage();
    lastOnLoadRegBranchTokenRef.current = null;
    setOnLoadRegBranchResponse({
      loading: false,
      success: null,
      message: null,
      data: null,
    });
    setIsLoggedIn(false);
    if (typeof navigate === 'function') {
      navigate('/login', { replace: true });
    }
  }, []);

  const regBranchSession = useMemo(
    () => parseRegBranchOnLoadData(onLoadRegBranchResponse?.data),
    [onLoadRegBranchResponse]
  );

  const value = useMemo(
    () => ({
      isLoggedIn,
      setIsLoggedIn,
      selectedMenuTab,
      setSelectedMenuTab,
      statsDataFromSocket,
      setStatsDataFromSocket,
      OnLoadRegBranchInformation,
      retryOnLoadRegBranchInformation,
      refreshRegBranchOnLoadInBackground,
      branchSessionLogout,
      onLoadRegBranchResponse,
      setOnLoadRegBranchResponse,
      regBranchSession,
      lastOnLoadRegBranchTokenRef,
      OnPhoneVerify,
      setOnPhoneVerifyResponse,
      onPhoneVerifyResponse,
      orderFromSocket,
      setOrderFromSocket,
    }),
    [
      isLoggedIn,
      selectedMenuTab,
      OnLoadRegBranchInformation,
      retryOnLoadRegBranchInformation,
      refreshRegBranchOnLoadInBackground,
      branchSessionLogout,
      onLoadRegBranchResponse,
      regBranchSession,
      OnPhoneVerify,
      onPhoneVerifyResponse,
      orderFromSocket,
      statsDataFromSocket,
    ]
  );

  return (
    <GlobalStateDataContext.Provider value={value}>{children}</GlobalStateDataContext.Provider>
  );
};

export default GlobalStateDataProvider;
